import {
  type ConvertFormatResponse,
  FORMAT_CONVERSION_CREDIT_COST,
  FORMAT_PRESET_DIMENSIONS,
  type FormatOutput,
  type FormatPreset,
} from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { Download, Wand2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useConvertFormat } from '~/features/ai-studio/hooks'
import { AiProcessingOverlay } from '~/features/shared/components/ai-processing-overlay'
import {
  CreditCostNote,
  CreditCostTag,
  useCreditCost,
} from '~/features/shared/components/credit-cost'
import { ImageUploadField } from '~/features/shared/components/image-upload-field'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import { Card, CardContent } from '~/shared/ui/card'
import { Label } from '~/shared/ui/label'
import { PulsifyLoader } from '~/shared/ui/pulsify-loader'
import { PageHeader } from '~/widgets/page-header'
import { FormatFocusField } from './format-focus-field'

const PRESETS: { key: FormatPreset; label: () => string }[] = [
  { key: 'instagram-square', label: m.formatPresetInstagramSquare },
  { key: 'instagram-portrait', label: m.formatPresetInstagramPortrait },
  { key: 'instagram-story', label: m.formatPresetInstagramStory },
  { key: 'facebook-post', label: m.formatPresetFacebookPost },
  { key: 'pulsify-cover', label: m.formatPresetPulsifyCover },
  { key: 'email-header', label: m.formatPresetEmailHeader },
]

const OUTPUTS: FormatOutput[] = ['jpeg', 'png', 'webp']

export function FormatConverterTool() {
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const convert = useConvertFormat(businessId)

  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<FormatPreset>>(new Set())
  const [output, setOutput] = useState<FormatOutput>('jpeg')
  const [focus, setFocus] = useState('')
  const [results, setResults] = useState<ConvertFormatResponse['assets']>([])

  const cost = selected.size * FORMAT_CONVERSION_CREDIT_COST
  const { state: creditState } = useCreditCost(businessId, cost)
  const canConvert =
    creditState !== 'short' && !!file && selected.size > 0 && !!businessId && !convert.isPending

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview)
    }
  }, [preview])

  function loadFile(picked: File | null) {
    setFile(picked)
    setPreview(picked ? URL.createObjectURL(picked) : null)
    setResults([])
  }

  function togglePreset(key: FormatPreset) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  async function handleConvert() {
    if (!file) return
    try {
      const res = await convert.mutateAsync({
        file,
        presets: [...selected],
        output,
        focus,
      })
      setResults(res.assets)
      toast.success(m.formatConverterDone())
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
    }
  }

  return (
    <div className="space-y-6">
      <AiProcessingOverlay open={convert.isPending} />
      <PageHeader title={m.formatConverter()} description={m.formatConverterPageDescription()} />

      <Card>
        <CardContent className="space-y-5 pt-6">
          <div className="space-y-2">
            <Label>{m.formatConverterSource()}</Label>
            <ImageUploadField file={file} preview={preview} onSelect={loadFile} />
          </div>

          <div className="space-y-2">
            <Label>{m.formatConverterPresets()}</Label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {PRESETS.map(({ key, label }) => {
                const dim = FORMAT_PRESET_DIMENSIONS[key]
                const active = selected.has(key)
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => togglePreset(key)}
                    className={`rounded-lg border p-3 text-left text-sm transition-colors ${
                      active ? 'border-primary bg-primary/10' : 'hover:bg-accent'
                    }`}
                  >
                    <span className="block font-medium">{label()}</span>
                    <span className="block text-xs text-muted-foreground">
                      {dim.width}×{dim.height}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          <FormatFocusField value={focus} onChange={setFocus} />

          <div className="space-y-2">
            <Label>{m.formatConverterOutput()}</Label>
            <div className="flex gap-2">
              {OUTPUTS.map((value) => (
                <Button
                  key={value}
                  type="button"
                  size="sm"
                  variant={output === value ? 'default' : 'outline'}
                  onClick={() => setOutput(value)}
                >
                  {value.toUpperCase()}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm text-muted-foreground">
              <CreditCostNote businessId={businessId} cost={cost} />
            </div>
            <Button disabled={!canConvert} onClick={handleConvert}>
              {convert.isPending ? (
                <PulsifyLoader className="size-5" tone="on-brand" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
              {convert.isPending ? m.formatConverterGenerating() : m.formatConverterGenerate()}
              {!convert.isPending && <CreditCostTag cost={cost} />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {results.length > 0 && (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <div className="flex items-center justify-between">
              <p className="font-medium">{m.formatConverterResults()}</p>
              <Button asChild variant="link" size="sm">
                <Link to="/dashboard/media">{m.mediaLibrary()}</Link>
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {results.map((asset) => (
                <div key={asset.id} className="space-y-1">
                  <div className="relative aspect-square overflow-hidden rounded-lg bg-muted">
                    <img src={asset.url} alt={asset.title} className="h-full w-full object-cover" />
                    <Button
                      asChild
                      size="icon"
                      variant="secondary"
                      className="absolute right-1 bottom-1"
                    >
                      <a href={asset.url} download target="_blank" rel="noreferrer">
                        <Download className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {asset.width}×{asset.height}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
